<form [formGroup]="intakeForm" (ngSubmit)="saveIntakeSettings()">
	<div class="col-12 text-right">
		<button mat-stroked-button class="mt-2" type="submit">
			<mat-icon>save</mat-icon> Save
		</button>
	</div>

	<p class="text-muted small mb-3">
		Poll a mailbox for operational mail (orders, invoices, remittances). Review and post from
		<strong>Orders → Business mail intake</strong>. Outbound SMTP and vendor discrepancy alerts stay on the Email Settings tab.
		<button mat-button type="button" class="ms-1 p-0 align-baseline" (click)="openIntakeHub()">Open intake hub</button>
	</p>

	<div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
		<button mat-stroked-button type="button" (click)="testMailboxConnection()" [disabled]="connectionTesting">
			Test mailbox connection
		</button>
		<span class="text-muted small" *ngIf="aiStatus">
			AI key: {{ aiStatus.api_key_configured ? 'configured' : 'not set' }}
			<span *ngIf="aiStatus.model"> · model {{ aiStatus.model }}</span>
		</span>
	</div>

	<div class="form-group">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>Intake mail provider</mat-label>
				<mat-select formControlName="config_mail_intake_provider">
					<mat-option value="microsoft_graph">Microsoft Graph</mat-option>
					<mat-option value="imap">IMAP</mat-option>
				</mat-select>
			</mat-form-field>
		</div>
	</div>
	<div class="form-group">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>Rejected intake retention (days)</mat-label>
				<input matInput type="number" formControlName="config_email_intake_retention_days" autocomplete="off" />
			</mat-form-field>
		</div>
	</div>

	<h6 class="text-secondary mt-3">Microsoft Graph</h6>
	<div class="form-group">
		<div class="col-sm-10">
			<mat-slide-toggle formControlName="config_mail_graph_enabled" color="primary">Enable Graph mailbox polling</mat-slide-toggle>
		</div>
	</div>
	<div class="form-group" *ngIf="intakeForm?.get('config_mail_graph_enabled')?.value">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>Mailbox to read (leave blank for default)</mat-label>
				<input matInput formControlName="config_mail_graph_mailbox" autocomplete="off" />
			</mat-form-field>
		</div>
	</div>
	<div class="form-group" *ngIf="intakeForm?.get('config_mail_graph_enabled')?.value">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>Folder name (e.g. Inbox, Orders)</mat-label>
				<input matInput formControlName="config_mail_graph_folder" autocomplete="off" />
			</mat-form-field>
		</div>
	</div>
	<div class="form-group" *ngIf="intakeForm?.get('config_mail_graph_enabled')?.value">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>Azure AD tenant ID (or domain)</mat-label>
				<input matInput formControlName="config_mail_graph_oauth_tenant_id" autocomplete="off" />
			</mat-form-field>
		</div>
	</div>
	<div class="form-group" *ngIf="intakeForm?.get('config_mail_graph_enabled')?.value">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>Application (client) ID</mat-label>
				<input matInput formControlName="config_mail_graph_oauth_client_id" autocomplete="off" />
			</mat-form-field>
		</div>
	</div>
	<div class="form-group" *ngIf="intakeForm?.get('config_mail_graph_enabled')?.value">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>Refresh token (stored after sign-in; optional manual paste)</mat-label>
				<input matInput formControlName="config_mail_graph_oauth_refresh_token" autocomplete="off" />
			</mat-form-field>
			<p class="text-muted small mb-2">
				Set server environment variable <code>MICROSOFT_MAIL_GRAPH_OAUTH_CLIENT_SECRET</code> to the app registration client secret. In Azure, add a web redirect URI pointing to this API: path
				<code>index.php?route=apir/globals/settings/mail_graph_read_oauth_callback</code> (full URL must match what users hit for the API).
			</p>
			<button mat-stroked-button type="button" (click)="startMicrosoftGraphOAuth()">Connect Microsoft for reading</button>
		</div>
	</div>

	<h6 class="text-secondary mt-4 pt-3 border-top">IMAP (when intake provider is IMAP)</h6>
	<div class="form-group" *ngIf="intakeForm?.get('config_mail_intake_provider')?.value === 'imap'">
		<div class="col-sm-10">
			<mat-form-field appearance="fill" class="w-100">
				<mat-label>IMAP host</mat-label>
				<input matInput formControlName="config_mail_imap_hostname" autocomplete="off" />
			</mat-form-field>
		</div>
	</div>
	<div class="form-group" *ngIf="intakeForm?.get('config_mail_intake_provider')?.value === 'imap'">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>IMAP port</mat-label>
				<input matInput type="number" formControlName="config_mail_imap_port" autocomplete="off" />
			</mat-form-field>
		</div>
	</div>
	<div class="form-group" *ngIf="intakeForm?.get('config_mail_intake_provider')?.value === 'imap'">
		<div class="col-sm-10">
			<mat-form-field appearance="fill" class="w-100">
				<mat-label>IMAP username</mat-label>
				<input matInput formControlName="config_mail_imap_username" autocomplete="off" />
			</mat-form-field>
		</div>
	</div>
	<div class="form-group" *ngIf="intakeForm?.get('config_mail_intake_provider')?.value === 'imap'">
		<div class="col-sm-10">
			<mat-form-field appearance="fill" class="w-100">
				<mat-label>IMAP password</mat-label>
				<input matInput type="password" formControlName="config_mail_imap_password" autocomplete="new-password" />
			</mat-form-field>
		</div>
	</div>
	<div class="form-group" *ngIf="intakeForm?.get('config_mail_intake_provider')?.value === 'imap'">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>IMAP folder</mat-label>
				<input matInput formControlName="config_mail_imap_folder" autocomplete="off" />
			</mat-form-field>
		</div>
	</div>

	<h5 class="mb-2 text-secondary mt-4 pt-3 border-top">AI-assisted intake (optional)</h5>
	<p class="text-muted small mb-2">
		Uses the same Anthropic API key as other ERP AI features when configured on the server. Rules-based classification and extraction still run when AI is off.
	</p>
	<div class="form-group" [formGroup]="aiForm">
		<div class="col-sm-10 d-flex flex-column gap-2">
			<mat-slide-toggle formControlName="enabled" color="primary">Enable AI features when server supports them</mat-slide-toggle>
			<mat-slide-toggle formControlName="classifierEnabled" color="primary">Use AI-assisted classification (when available)</mat-slide-toggle>
			<mat-slide-toggle formControlName="extractorEnabled" color="primary">Use AI-assisted field extraction (when available)</mat-slide-toggle>
		</div>
	</div>
	<div class="form-group" [formGroup]="aiForm">
		<div class="col-sm-10">
			<mat-form-field appearance="fill" class="w-100">
				<mat-label>Preferred model id (optional)</mat-label>
				<input matInput formControlName="model" autocomplete="off" />
				<mat-hint>Server-specific; leave blank to use server default when AI is wired.</mat-hint>
			</mat-form-field>
		</div>
	</div>
	<div class="form-group" [formGroup]="aiForm">
		<div class="col-sm-10">
			<mat-form-field appearance="fill" class="w-100">
				<mat-label>Extra instructions for extraction / classification (optional)</mat-label>
				<textarea matInput formControlName="instructions" rows="4" autocomplete="off"></textarea>
			</mat-form-field>
		</div>
	</div>
</form>
